'use client';

import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Search, MapPin, ChevronDown, X } from 'lucide-react';
import Image from 'next/image';
/* eslint-disable @next/next/no-img-element */
import styles from './HomeHero.module.scss';
import type { ListingTypeOption } from './HeroSearch';
import SearchSuggestionsDropdown from './SearchSuggestionsDropdown';
import { useSearchAutocomplete } from '@/hotdesk-ui/hooks/useSearchAutocomplete';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { HERO_SEARCH_MARKER } from '@/hotdesk-ui/constants/layout.constant';
export interface HomeHeroProps {
  autocompleteInput: MutableRefObject<any>;
  autocompleteContainer: MutableRefObject<any>;
  getPermission: () => void;
  sentData: () => void;
  addFilters: (value: number) => void;
  listingType: number;
  listingTypes?: ListingTypeOption[];
  heroImages?: string[];
  /** Current resolved location text — keeps the clear (X) button in sync. */
  searchDisplayKey?: string;
  /** Called when a place is picked from the suggestions dropdown. */
  onSelect: (result: GeoCodeResult) => void;
}
const DEFAULT_HERO_IMAGES = ['https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4/hero-bg.jpg', 'https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4/hero-bg-2.jpg', 'https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4/hero-bg-3.jpg', 'https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4/hero-bg-4.jpg'];
const HERO_INTERVAL_MS = 5000;
const HomeHero: FC<HomeHeroProps> = ({
  autocompleteInput,
  autocompleteContainer,
  getPermission,
  sentData,
  addFilters,
  listingType,
  listingTypes = [],
  heroImages,
  searchDisplayKey,
  onSelect
}) => {
  const t = useTranslations();
  const tExplore = useTranslations('explore');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const {
    containerRef,
    predictions,
    activeIndex,
    isOpen: suggestionsOpen,
    setActiveIndex,
    selectByIndex,
    selectActiveOrFirst,
    resetToEmpty
  } = useSearchAutocomplete({
    inputRef: autocompleteInput,
    onSelect,
    // `onSelect` only records the place here — the push to explore is
    // `sentData`, so Enter has to do both, same as the search button below.
    onEnterSelect: () => sentData()
  });
  const syncSearchValue = useCallback(() => {
    const value = autocompleteInput.current?.value?.trim() ?? '';
    setHasSearchValue(value.length > 0);
  }, [autocompleteInput]);
  const handleClearSearch = () => {
    if (autocompleteInput.current) autocompleteInput.current.value = '';
    setHasSearchValue(false);
    autocompleteInput.current?.focus();
    // Wiping the value in code fires no input event — drop the predictions
    // for the erased query explicitly.
    resetToEmpty();
  };

  // Reflect the resolved location so the clear "X" shows on load even when the
  // value is set programmatically. Direct set (no input event) keeps the
  // suggestions list closed.
  useEffect(() => {
    const input = autocompleteInput.current;
    if (input && searchDisplayKey && input.value !== searchDisplayKey) {
      input.value = searchDisplayKey;
    }
    syncSearchValue();
  }, [searchDisplayKey, syncSearchValue, autocompleteInput]);

  // Search button: if the user typed but didn't pick a suggestion, resolve the
  // highlighted/first one, then run the search once its location is applied.
  const handleSearchClick = () => {
    if (!selectActiveOrFirst(() => sentData())) sentData();
  };
  const images = heroImages && heroImages.length > 0 ? heroImages : DEFAULT_HERO_IMAGES;
  const [activeImage, setActiveImage] = useState(0);
  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setActiveImage(prev => (prev + 1) % images.length);
    }, HERO_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [images.length]);
  const allOption: ListingTypeOption = {
    id: 0,
    type: t('listingType.All')
  };
  const typeOptions = [allOption, ...listingTypes];
  const selectedType = typeOptions.find(o => o.id === listingType) || allOption;
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelectType = (option: ListingTypeOption) => {
    addFilters(option.id);
    setDropdownOpen(false);
  };
  return <section className={styles.hero} data-sentry-component="HomeHero" data-sentry-source-file="HomeHero.tsx">
            <div className={styles.bgImage}>
                {images.map((src, idx) => <Image key={src} src={src} alt="" fill priority={idx === 0} className={styles.bgImg} style={{
        opacity: idx === activeImage ? 1 : 0,
        transition: 'opacity 1s ease-in-out'
      }} />)}
                <div className={styles.overlay} />
                <img src="/assets/icons/morphy/header_face.svg" alt="" aria-hidden="true" className={styles.morphy} />
            </div>

            <div className={styles.content}>
                <h1 className={styles.heading}>
                    {t.rich('header.title', {
          em: chunks => <em className={styles.headingEm}>{chunks}</em>
        })}
                </h1>

                {/* Marked so the header holds back its own search bar while
                    this one is on screen — see HERO_SEARCH_MARKER. */}
                <div {...HERO_SEARCH_MARKER} className={styles.searchBar} ref={node => {
        autocompleteContainer.current = node;
        containerRef.current = node;
      }}>
                    <div className={styles.typeDropdownWrapper} ref={dropdownRef}>
                        <button type="button" className={styles.typeDropdown} onClick={() => setDropdownOpen(prev => !prev)}>
                            <span className={styles.typeLabel}>
                                {selectedType.type}
                            </span>
                            <ChevronDown size={16} data-sentry-element="ChevronDown" data-sentry-source-file="HomeHero.tsx" />
                        </button>
                        {dropdownOpen && <div className={styles.typeMenu}>
                                {typeOptions.map(option => <button key={option.id} type="button" className={styles.typeMenuItem} onClick={() => handleSelectType(option)}>
                                        {option.icon && <img src={option.icon} alt="" width={18} height={18} className={styles.typeMenuIcon} />}
                                        {option.type}
                                    </button>)}
                            </div>}
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.searchInputWrapper}>
                        <button type="button" className={styles.locationBtn} onClick={getPermission} aria-label="Detect location">
                            <MapPin size={18} data-sentry-element="MapPin" data-sentry-source-file="HomeHero.tsx" />
                        </button>
                        <input ref={autocompleteInput} type="text" className={styles.searchInput} placeholder={t('search-placeholder')} autoCorrect="off" autoCapitalize="off" spellCheck={false} onInput={syncSearchValue} onChange={syncSearchValue} />
                        <button type="button" className={`${styles.clearBtn} ${hasSearchValue ? styles.clearBtnVisible : ''}`} onClick={handleClearSearch} aria-label={tExplore('clearSearch')} aria-hidden={!hasSearchValue} tabIndex={hasSearchValue ? 0 : -1}>
                            <X size={16} strokeWidth={2.25} data-sentry-element="X" data-sentry-source-file="HomeHero.tsx" />
                        </button>
                    </div>
                    <button type="button" className={styles.searchButton} onClick={handleSearchClick} aria-label="Search">
                        <Search size={20} color="#fff" data-sentry-element="Search" data-sentry-source-file="HomeHero.tsx" />
                    </button>
                    {suggestionsOpen && <SearchSuggestionsDropdown predictions={predictions} activeIndex={activeIndex} onHover={setActiveIndex} onSelect={selectByIndex} />}
                </div>

                <div className={styles.appButtons}>
                    <a href="https://apps.apple.com/us/app/hotdesk/id1484609853" className={styles.storeBtn} target="_blank" rel="noopener noreferrer">
                        <Image src="https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/icons/apple.svg" alt="" width={24} height={24} data-sentry-element="Image" data-sentry-source-file="HomeHero.tsx" />
                        <div className={styles.storeText}>
                            <span className={styles.storeSmall}>DOWNLOAD ON THE</span>
                            <span className={styles.storeName}>App Store</span>
                        </div>
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=io.hotdesk.app" className={styles.storeBtn} target="_blank" rel="noopener noreferrer">
                        <Image src="https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/icons/g_play.svg" alt="" width={24} height={24} data-sentry-element="Image" data-sentry-source-file="HomeHero.tsx" />
                        <div className={styles.storeText}>
                            <span className={styles.storeSmall}>GET IT ON</span>
                            <span className={styles.storeName}>Google Play</span>
                        </div>
                    </a>
                </div>
            </div>
        </section>;
};
export default HomeHero;